/**
 * The composer's From field: which of the mailbox's addresses the letter
 * leaves from.
 *
 * A row of its own at the top, beside To and the subject, because the owner
 * asked for a From field there and because it is part of the letter's address
 * block. It used to be a grey word at the end of the send row, which could be
 * read but not changed.
 *
 * With one address there is nothing to pick, so the row shows it as text: a
 * picker with one choice reads as a control that is broken. The list is the
 * one the settings switched on (`GET /mail/senders`); the server refuses any
 * other address, so this cannot offer one that would not go.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Combobox } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { getMailSenders, type ComposerSender } from "../../../lib/mail";

export interface SenderSet {
  rows: ComposerSender[];
  defaultAddress: string;
}

/**
 * The mailbox's switched-on sending addresses, fetched once per composer and
 * again on `reload`. The default is the writer's own when they set one.
 *
 * Null while it is still asking, and stays null when the request fails: the
 * composer then shows the address it was given and sends without naming one,
 * which is the default sender - the letter still goes as it did before.
 */
export function useMailSenders(): (SenderSet & { reload: () => void }) | null {
  const [set, setSet] = useState<SenderSet | null>(null);
  // Asked again after the writer changed their own default sender, so a
  // letter nobody picked a sender for follows it.
  const [round, setRound] = useState(0);
  const reload = useCallback(() => setRound((n) => n + 1), []);
  useEffect(() => {
    let alive = true;
    getMailSenders()
      .then((r) => alive && setSet({ rows: r.senders, defaultAddress: r.defaultAddress }))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [round]);
  return useMemo(() => (set ? { ...set, reload } : null), [set, reload]);
}

/** "Name <address>", or the bare address when it has no name: the From line as sent. */
export function senderLine(s: ComposerSender | undefined, fallback: string): string {
  if (!s) return fallback;
  return s.name.trim() ? `${s.name.trim()} <${s.address}>` : s.address;
}

export function FromRow({
  set,
  value,
  onChange,
  fallback,
  disabled,
}: {
  set: SenderSet | null;
  value: string;
  onChange: (address: string) => void;
  /** Shown while the list loads, or when it could not be read. */
  fallback: string;
  disabled?: boolean;
}) {
  const t = useT();
  const rows = set?.rows ?? [];
  const current = rows.find((r) => r.address === value);

  return (
    <div className="flex min-h-[46px] items-center gap-2.5 border-b border-[var(--crm-hair)] px-[22px]">
      <span id="crm-comp-from" className="w-11 flex-none text-xs font-[650] text-muted-foreground">
        {t("compose.fromLabel")}
      </span>
      {rows.length > 1 ? (
        // The open list is portalled out of the window but still bubbles
        // through it in React, and the composer closes itself on Escape.
        // Escape inside the list closes the list only; on the field itself it
        // still closes the window.
        <div
          className="min-w-0 flex-1"
          onKeyDown={(e) => {
            if (e.key === "Escape" && (e.target as HTMLElement).closest("[data-radix-popper-content-wrapper]")) {
              e.stopPropagation();
            }
          }}
        >
          <Combobox
            options={rows.map((r) => ({ value: r.address, label: r.address, hint: r.name || undefined }))}
            value={value}
            onChange={(v) => v && onChange(v)}
            disabled={disabled}
            aria-labelledby="crm-comp-from"
            // The row is the field, the way To and the subject are: no box
            // inside a box, only the chevron to say it opens. Important,
            // because the CRM dresses every combobox trigger as a sunken pill
            // in unlayered CSS (index.css), which outranks a utility.
            className="h-11 w-full border-0 !rounded-none !bg-transparent px-0 text-[13.25px] font-[650] !shadow-none"
            popoverClassName="rounded-2xl"
            placeholder={t("compose.fromPick")}
            searchPlaceholder={t("compose.fromSearch")}
            emptyLabel={t("compose.fromNone")}
          />
        </div>
      ) : (
        <span className="crm-trunc min-w-0 flex-1 text-[13.25px] font-[650]" title={senderLine(current, value || fallback)}>
          {senderLine(current, value || fallback)}
        </span>
      )}
    </div>
  );
}
